import { useState } from 'react'
import { X, Loader2, Building2, Briefcase, CheckCircle2, AlertTriangle } from 'lucide-react'
import { api } from '../../../services/api'
import type { Deal } from './fiTypes'
import { formatCurrency } from './fiFormat'

interface ConvertDealModalProps {
  deal: Deal
  onClose: () => void
  /** Called after a successful conversion so the caller can refetch. */
  onConverted: () => void
}

/** Which entities `convertDeal` will create for each deal type. Mirrors
 *  the tenant/client conventions used elsewhere: managed → tenant,
 *  professional → client, hybrid → tenant + a client linked to it. */
function targetEntities(dealType: Deal['dealType']): Array<{ kind: 'tenant' | 'client'; label: string }> {
  if (dealType === 'managed') return [{ kind: 'tenant', label: 'Tenant (Seguridad Gestionada)' }]
  if (dealType === 'professional') return [{ kind: 'client', label: 'Cliente (Servicios Profesionales)' }]
  return [
    { kind: 'tenant', label: 'Tenant (Seguridad Gestionada)' },
    { kind: 'client', label: 'Cliente hibrido, vinculado al tenant' },
  ]
}

export default function ConvertDealModal({ deal, onClose, onConverted }: ConvertDealModalProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const alreadyConverted = !!(deal.convertedTenantId || deal.convertedClientId)
  const notWon = deal.stage !== 'won'
  const entities = targetEntities(deal.dealType)

  const handleConvert = async () => {
    setBusy(true)
    setError(null)
    const res = await api.convertDeal(deal.id)
    setBusy(false)
    if (!res.success) {
      setError(res.error || 'Error al convertir el deal')
      return
    }
    onConverted()
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-lg w-full border border-gray-700">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Convertir Deal</h2>
          <button onClick={onClose} className="text-gray-300 hover:text-white p-2 rounded-lg hover:bg-gray-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {error && (
            <div className="bg-severity-critical/20 border border-severity-critical text-severity-critical px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {alreadyConverted ? (
            <div className="bg-green-500/10 border border-green-500/30 text-green-200 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                Este deal ya fue convertido.
                {deal.convertedTenantId && <div className="text-xs text-green-300/80 mt-1">Tenant: {deal.convertedTenantId}</div>}
                {deal.convertedClientId && <div className="text-xs text-green-300/80 mt-1">Cliente: {deal.convertedClientId}</div>}
              </div>
            </div>
          ) : (
            <>
              {notWon && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Este deal todavia no esta en etapa "Ganado". Se recomienda marcarlo como ganado antes de convertirlo.
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-2">Se creara</h3>
                <div className="space-y-2">
                  {entities.map(entity => (
                    <div key={entity.kind} className="flex items-center gap-3 bg-gray-900/50 border border-gray-700 rounded-lg px-3 py-2">
                      {entity.kind === 'tenant'
                        ? <Building2 className="w-4 h-4 text-accent flex-shrink-0" />
                        : <Briefcase className="w-4 h-4 text-accent flex-shrink-0" />}
                      <span className="text-sm text-gray-200">{entity.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-2">Con estos valores iniciales</h3>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-gray-500">Nombre</dt>
                  <dd className="text-gray-200 text-right">{deal.name}</dd>

                  {deal.plan && (
                    <>
                      <dt className="text-gray-500">Plan</dt>
                      <dd className="text-gray-200 text-right">{deal.plan}</dd>
                    </>
                  )}

                  {deal.estimatedUsers != null && (
                    <>
                      <dt className="text-gray-500">Usuarios estimados</dt>
                      <dd className="text-gray-200 text-right">{deal.estimatedUsers}</dd>
                    </>
                  )}

                  <dt className="text-gray-500">MRR proyectado</dt>
                  <dd className="text-gray-200 text-right">{formatCurrency(deal.projectedMrr, deal.currency)}</dd>

                  <dt className="text-gray-500">Grupo</dt>
                  <dd className="text-gray-200 text-right">{deal.isIntragroup ? 'Intra-grupo' : 'Externo'}</dd>

                  {deal.contactName && (
                    <>
                      <dt className="text-gray-500">Contacto</dt>
                      <dd className="text-gray-200 text-right">{deal.contactName}</dd>
                    </>
                  )}

                  {deal.contactEmail && (
                    <>
                      <dt className="text-gray-500">Email</dt>
                      <dd className="text-gray-200 text-right">{deal.contactEmail}</dd>
                    </>
                  )}
                </dl>
              </div>

              <p className="text-xs text-gray-500">
                El MRR proyectado deja de ser pipeline y pasa a facturacion real una vez
                que cargues el servicio/plan correspondiente en el nuevo registro.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white transition-colors">
            {alreadyConverted ? 'Cerrar' : 'Cancelar'}
          </button>
          {!alreadyConverted && (
            <button
              onClick={handleConvert}
              disabled={busy}
              className="bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition-colors flex items-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Convertir
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
